import { type FC, useState } from "react";
import { Link } from "react-router-dom";

import { Button, Headline, Paragraph, Select, type SelectItem, Small } from "@fronterahealth/frontera-ui-components";

import { type OrganizationSummary, useListPayorTypes, useListRulePackages } from "@api/rest/types-and-hooks";
import { notifyError } from "@components/notifications/notifications";
import { OnOffSwitch } from "@pages/QARules/OnOffSwitch";
import { PackageEditAction } from "@pages/QARules/PackageEditAction";
import { RulePreview } from "@pages/QARules/RulePreview";
import { PackageType, QARulesScope } from "@pages/QARules/constants";
import { type PackageOwnerContext, packageHref } from "@pages/QARules/qaRuleLabels";
import { packageControl, ruleControl, toggleValueFor } from "@pages/QARules/scopeControls";
import { type ToggleValue, useQARuleApi } from "@pages/QARules/useQARuleApi";

interface ScopedQARulesProps {
  scope: QARulesScope;
  /** Org id or template id, matching `scope`. */
  scopeId: number;
  /** Template scope: the orgs the template is assigned to, offered in the preview. */
  organizationChoices?: readonly OrganizationSummary[];
}

const SCOPE_COPY: Record<
  QARulesScope,
  { attachTitle: string; attachHelp: string; previewTitle: string; previewHelp: string }
> = {
  [QARulesScope.TEMPLATE]: {
    attachTitle: "Attach a package to this template",
    attachHelp: "Attached packages apply to every report on this template, in every org that uses it.",
    previewTitle: "What this template gets",
    previewHelp:
      "Pick one of the template's orgs to also see that org's packages. Switches here turn packages and rules off for this template, in every org.",
  },
  [QARulesScope.ORGANIZATION]: {
    attachTitle: "Attach a package to this organization",
    attachHelp: "Attached packages apply to every report in this org, on every template.",
    previewTitle: "What this organization gets",
    previewHelp:
      "Pick a template to also see its packages and payor. Switches here turn packages and rules off for this org, on every template.",
  },
};

/**
 * QA settings for one org or one template: attach packages at this level, and a
 * preview of what a report gets where every switch writes rows at this level only.
 */
export const ScopedQARules: FC<ScopedQARulesProps> = ({ scope, scopeId, organizationChoices }) => {
  const qaRuleApi = useQARuleApi();
  const isTemplate = scope === QARulesScope.TEMPLATE;
  const { data: packageData } = useListRulePackages();
  const packages = packageData?.status === 200 ? packageData.data.results : [];
  const { data: attachedData } = useListRulePackages(
    isTemplate ? { attached_to_report_template_id: scopeId } : { attached_to_organization_id: scopeId },
  );
  const attached = attachedData?.status === 200 ? attachedData.data.results : [];
  const { data: payorData } = useListPayorTypes();
  const payorTypes = payorData?.status === 200 ? payorData.data.results : [];
  const [toAttach, setToAttach] = useState<SelectItem | null>(null);

  const context: PackageOwnerContext = isTemplate ? { reportTemplateId: scopeId } : { organizationId: scopeId };
  const copy = SCOPE_COPY[scope];

  const setPackage = (ruleSetId: number, value: ToggleValue) =>
    isTemplate
      ? qaRuleApi.setTemplatePackage(scopeId, ruleSetId, value)
      : qaRuleApi.setOrgPackage(scopeId, ruleSetId, value);
  const setRule = (ruleId: number, value: ToggleValue) =>
    isTemplate ? qaRuleApi.setTemplateRule(scopeId, ruleId, value) : qaRuleApi.setOrgRule(scopeId, ruleId, value);

  const withErrorToast = async (action: () => Promise<unknown>) => {
    try {
      await action();
    } catch (e) {
      notifyError(e instanceof Error ? e.message : "Something went wrong.");
    }
  };

  // Base and insurance packages apply on their own; only custom ones need attaching.
  const attachedIds = new Set(attached.map((pkg) => pkg.id));
  const attachItems: SelectItem[] = packages
    .filter((pkg) => pkg.package_type === PackageType.CUSTOM && !attachedIds.has(pkg.id))
    .map((pkg) => ({ id: String(pkg.id), primary: pkg.name, secondary: `${pkg.rules.length} rules` }));

  return (
    <div className="flex max-w-5xl flex-col gap-10">
      <section className="flex flex-col gap-2">
        <Headline size="extra-small">{copy.attachTitle}</Headline>
        <Paragraph className="text-text-secondary">{copy.attachHelp}</Paragraph>
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <Select
              title="Package"
              items={attachItems}
              placeholderText="Search custom packages"
              enableSearch
              selected={toAttach}
              setSelected={setToAttach}
            />
          </div>
          <Button
            variant="primary"
            buttonStyle="filled"
            size="sm"
            text="Attach"
            disabled={!toAttach?.id}
            onClick={() =>
              withErrorToast(async () => {
                await setPackage(Number(toAttach?.id), true);
                setToAttach(null);
              })
            }
          />
        </div>
        {attached.length > 0 && (
          <div className="mt-2 flex flex-col divide-y divide-border-subtle rounded border border-border-subtle">
            {attached.map((pkg) => (
              <div key={pkg.id} className="flex items-center justify-between gap-3 px-4 py-2">
                <Link to={packageHref(pkg.id)} className="truncate text-sm text-text-primary hover:underline">
                  {pkg.name}
                </Link>
                <div className="flex shrink-0 items-center gap-3">
                  <Small colorType="secondary">
                    {pkg.rules.length} rule{pkg.rules.length === 1 ? "" : "s"}
                  </Small>
                  <Button
                    variant="secondary"
                    buttonStyle="border"
                    size="xs"
                    text="Detach"
                    onClick={() => withErrorToast(() => setPackage(pkg.id, null))}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="flex flex-col gap-2">
        <Headline size="extra-small">{copy.previewTitle}</Headline>
        <Paragraph className="text-text-secondary">{copy.previewHelp}</Paragraph>
        {isTemplate && organizationChoices?.length === 0 && (
          <Paragraph className="text-text-secondary">
            This template isn&apos;t assigned to any organizations yet. Assign them on the Details tab to preview what
            each org gets.
          </Paragraph>
        )}
        <RulePreview
          organizationChoices={organizationChoices}
          fixedTemplateId={isTemplate ? scopeId : undefined}
          fixedOrganizationId={isTemplate ? undefined : scopeId}
          renderPackageAction={(pkg) => {
            const control = packageControl(pkg, scope);
            const fullPackage = packages.find((p) => p.id === pkg.id);
            return (
              <>
                <PackageEditAction packageId={pkg.id} pkg={fullPackage} context={context} payorTypes={payorTypes} />
                {control.detach ? (
                  <Button
                    variant="secondary"
                    buttonStyle="border"
                    size="xs"
                    text="Detach"
                    onClick={() => withErrorToast(() => setPackage(pkg.id, null))}
                  />
                ) : (
                  <OnOffSwitch
                    label={`${pkg.name} on`}
                    checked={control.checked}
                    disabled={control.disabled}
                    title={control.reason}
                    onChange={(checked) => withErrorToast(() => setPackage(pkg.id, toggleValueFor(checked)))}
                  />
                )}
              </>
            );
          }}
          renderRuleAction={(pkg, rule) => {
            const control = ruleControl(pkg, rule, scope);
            return (
              <OnOffSwitch
                label={`${rule.rule.name || "Rule"} on`}
                checked={control.checked}
                disabled={control.disabled}
                title={control.reason}
                onChange={(checked) => withErrorToast(() => setRule(rule.rule.id, toggleValueFor(checked)))}
              />
            );
          }}
        />
      </section>
    </div>
  );
};
